import type { BnplMethodType } from '@/utils/bnpl';

import type {
  MessagingCountry,
  MessagingCurrency,
} from './bnplInstallmentConstants';
import {
  COUNTRY_NAMES_TO_CODES,
  MESSAGING_COUNTRIES,
  MESSAGING_CURRENCIES,
} from './bnplInstallmentConstants';

export type ResolvedMessagingConfig = {
  amount: number;
  currency: MessagingCurrency;
  country: MessagingCountry;
  paymentMethodTypes: BnplMethodType[];
};

type ResolveMessagingConfigInput = {
  amountCents: number;
  currency: string;
  country: string;
  eligibleMethods: BnplMethodType[];
};

const isMessagingCurrency = (value: string): value is MessagingCurrency =>
  Object.prototype.hasOwnProperty.call(MESSAGING_CURRENCIES, value);

const toMessagingCurrency = (currency: string): MessagingCurrency | null => {
  const normalized = currency.toUpperCase();
  return isMessagingCurrency(normalized) ? normalized : null;
};

const isMessagingCountry = (value: string): value is MessagingCountry =>
  Object.prototype.hasOwnProperty.call(MESSAGING_COUNTRIES, value);

const toMessagingCountry = (country: string): MessagingCountry | null => {
  const normalized = country.trim().toUpperCase();
  if (isMessagingCountry(normalized)) return normalized;
  return COUNTRY_NAMES_TO_CODES[normalized] ?? null;
};

/**
 * Stripe never resolves the message for an unsupported currency/country and
 * reports no error, so gate here and render nothing instead of a row that
 * sits blank forever. A zero amount has no plan either (Stripe answers
 * `no_content`), so it resolves to nothing as well.
 */
export const resolveMessagingConfig = ({
  amountCents,
  currency,
  country,
  eligibleMethods,
}: ResolveMessagingConfigInput): ResolvedMessagingConfig | null => {
  const messagingCurrency = toMessagingCurrency(currency);
  const messagingCountry = toMessagingCountry(country);
  const amount = Math.round(amountCents);

  if (
    !messagingCurrency ||
    !messagingCountry ||
    eligibleMethods.length === 0 ||
    amount <= 0
  ) {
    return null;
  }

  return {
    amount,
    currency: messagingCurrency,
    country: messagingCountry,
    paymentMethodTypes: eligibleMethods,
  };
};
